import React, { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemText, Pagination, Stack, TextField, Typography } from "@mui/material";
import { useGetNotificationsQuery } from "../../../hooks/ReactQueryHooks/useGetNotificationsQuery";
import { useSchedulingOrg } from "../../../hooks/ReactQueryHooks/useSchedulingOrg";
import OrganizationSelect from "../Scheduling/OrganizationSelect";
import AppointmentDetailModal from "../Scheduling/appointments/AppointmentDetailModal";
import { getAppointment } from "../../api/Scheduling/appointments";
import { schedulingNotifications, markSchedulingNotificationRead } from "../../api/Scheduling/notifications";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import { updateNotificationCount } from "../../api/Notification";
import { decryptData } from "../../EncryptDecrypt";
import { withLocalTime, ViewDateTime } from "../../../utils/DateSupport";
import { mergeNotifications, notificationScheduleLink, schedulingInboxQueryKey } from "./notificationModel";

const ignoreCount = () => {};
export default function UserNotificationsList({ dashboard = false, setCount = ignoreCount }) {
  const org = useSchedulingOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState(false);
  const enabled = !!org.practitionerRef;
  const legacy = useGetNotificationsQuery({ callMe: enabled, enabled });
  const queryKey = schedulingInboxQueryKey({ userSub: org.user?.sub, practitionerRef: org.practitionerRef, organizationRef: org.organizationRef });
  const scheduling = useInfiniteQuery(queryKey, ({ pageParam }) => schedulingNotifications(org.organizationRef, pageParam), {
    enabled: enabled && !!org.organizationRef, getNextPageParam: data => data.nextCursor || undefined, refetchInterval: 60000,
  });
  const unread = Number(legacy.data?.unreadNotifications || 0) + Number(scheduling.data?.pages?.[0]?.unreadCount || 0);
  useEffect(() => { setCount(unread); }, [unread, setCount]);
  useEffect(() => { setSelected(null); setAppointment(null); setPage(1); setError(""); }, [org.organizationRef]);
  const rows = useMemo(() => mergeNotifications(legacy.data?.data || [], scheduling.data?.pages?.flatMap(item => item.notifications) || []), [legacy.data, scheduling.data]);
  const filtered = rows.filter(item => `${item.title} ${decryptData(item.message)}`.toLowerCase().includes(search.toLowerCase()));
  const size = dashboard ? 7 : 10;
  const view = async (item) => {
    setSelected(item); setError("");
    if (item.markAsRead) return;
    try {
      if (item.source === "scheduling") {
        await markSchedulingNotificationRead(item.organizationRef, item.notificationId);
        await queryClient.invalidateQueries(queryKey);
      } else {
        await updateNotificationCount({ patientId: item.id, notificationId: item.notificationId, markAsRead: true });
        await legacy.refetch();
      }
    } catch (failure) { setError(schedulingErrorMessage(failure, "Could not mark notification as read.")); }
  };
  const openTarget = async () => {
    const target = selected?.target;
    if (!target) return;
    const scheduleLink = notificationScheduleLink(target);
    if (scheduleLink) { navigate(scheduleLink); return; }
    if (target.appointmentId) {
      setOpening(true); setError("");
      try {
        const result = await getAppointment({ organizationRef: target.organizationRef, appointmentId: target.appointmentId });
        setAppointment(result.appointment || result); setSelected(null);
      } catch (failure) { setError(schedulingErrorMessage(failure, "This appointment is no longer available.")); }
      finally { setOpening(false); }
    }
  };
  return <Box component="section" sx={{ p: dashboard ? 0 : 3 }}>
    <Stack spacing={2}>
      {!dashboard && <OrganizationSelect context={org} inputId="inbox-organization" />}
      <Typography variant="caption" color="text.secondary">Scheduling notifications: {org.organizationName || "Select an organization"}</Typography>
      <TextField size="small" label="Search notifications" value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} sx={{ "& input": { height: "auto" } }} />
      {(legacy.isError || scheduling.isError) && <Alert severity="error" action={<Button onClick={() => { legacy.refetch(); scheduling.refetch(); }}>Retry</Button>}>Some notifications could not be loaded.</Alert>}
      {error && !selected && <Alert severity="error">{error}</Alert>}
      {(legacy.isLoading || scheduling.isLoading) && <CircularProgress size={24} aria-label="Loading notifications" />}
      <List disablePadding>{filtered.slice((page - 1) * size, page * size).map(item => <ListItemButton key={`${item.source}:${item.notificationId}`} onClick={() => view(item)} sx={{ borderBottom: "1px solid #e3e9ee", backgroundColor: item.markAsRead ? "transparent" : "#f0f7fc", alignItems: "flex-start" }}>
        <Box aria-label={item.markAsRead ? "Read" : "Unread"} sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: item.markAsRead ? "transparent" : "#005da8", mt: 1.8, mr: 1.5, flexShrink: 0 }} />
        <ListItemText primary={item.title} secondary={<>{withLocalTime(decryptData(item.message))}<br />{ViewDateTime(item.timestamp)}</>} primaryTypographyProps={{ fontWeight: item.markAsRead ? 400 : 600 }} secondaryTypographyProps={{ sx: { overflowWrap: "anywhere" } }} />
      </ListItemButton>)}</List>
      {!filtered.length && !legacy.isLoading && !scheduling.isLoading && <Typography color="text.secondary">No notifications.</Typography>}
      {filtered.length > size && <Pagination count={Math.ceil(filtered.length / size)} page={page} onChange={(_, value) => setPage(value)} />}
      {scheduling.hasNextPage && <Button disabled={scheduling.isFetchingNextPage} onClick={() => scheduling.fetchNextPage()}>Load older scheduling notifications</Button>}
    </Stack>
    <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="sm" aria-labelledby="inbox-message-title">
      <DialogTitle id="inbox-message-title">{selected?.title || "Notification"}</DialogTitle>
      <DialogContent>{error && <Alert severity="error">{error}</Alert>}<Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{withLocalTime(decryptData(selected?.message || ""))}</Typography></DialogContent>
      <DialogActions><Button onClick={() => setSelected(null)}>Close</Button>{selected?.target && <Button disabled={opening} onClick={openTarget}>{selected.target.appointmentId ? "Open appointment" : "Open schedule"}</Button>}</DialogActions>
    </Dialog>
    {appointment && <AppointmentDetailModal appointment={appointment} organizationRef={appointment.organizationRef} onHide={() => setAppointment(null)} onChanged={() => setAppointment(null)} />}
  </Box>;
}
